import { SoundStatus } from '@/views/system-config/notification-center/constant';
import store from '@/store/index';
import { safeGet } from '@/utils/safeGet';
import SuccessSoundPath from '@/assets/audio/success-notification-sound.mp3';
import ScanErrorAlertPath from '@/assets/audio/scan_error_alert.mp3';
import { errorReport } from '@/utils/tools';
import { i18n } from '@/i18n';
import Vue from 'vue';
import { getCookie } from './cookie';
const vue = new Vue();
initVoice();
type NotificationSoundConfigItem = {
  id: number;
  node_name: string;
  sound_type: number;
};
const localeToLangMap: { [key: string]: string } = {
  'en': 'en-US', // English
  'zh-CN': 'zh-CN', // 中文
  'zh-TW': 'zh-TW', // 中文
  'id': 'id-ID', // Bahasa Indonesia
  'ms-MY': 'ms-MY', // Bahasa Malayu
  'vi': 'vi-VN', // Vietnamese
  'th': 'th-TH', // Thai
  'pt-BR': 'pt-BR', // Portuguese
  'sp-MX': 'es-MX', // Spanish
  'tl-PH': 'tl-PH', // Tagalog
};
const langToDescMap = {
  'en-US': 'English',
  'id-ID': 'Bahasa Indonesia',
  'ms-MY': 'Bahasa Malayu',
  'pt-BR': 'Portuguese',
  'sp-MX': 'Spanish',
  'th-TH': 'Thai',
  'tl-PH': 'Tagalog',
  'vi-VN': 'Vietnamese',
  'zh-CN': '简体中文',
  'zh-TW': '繁体中文',
};
// 语言包配置文档链接
const docUrl =
  'https://docs.google.com/document/d/1lOKQ8kvsaheEvzsLUsTYscC47JnzcT8KPodekpo6a0I/edit?usp=sharing';
/**
 * 操作声音提示配置处理函数
 * @param operationNodeName 节点名称
 */
export function notificationSoundProcess(operationNodeName: string) {
  return new Promise((resolve) => {
    const config: NotificationSoundConfigItem[] =
      safeGet(store.state.notification, 'notificationSoundConfig') || [];
    if (
      config
        .filter((item) => item.sound_type === SoundStatus.Open)
        .map((item) => item.node_name)
        .includes(operationNodeName)
    ) {
      const audio = new Audio(SuccessSoundPath);
      audio.play();
    }
    resolve(0);
  });
}

export function errorNotificationSound() {
  return new Promise((resolve) => {
    const audio = new Audio(ScanErrorAlertPath);
    audio.play();
    resolve(0);
  });
}

// 提前初始化声音列表
export function initVoice() {
  window.speechSynthesis.getVoices();
}

export function checkCurrentLocaleVoiceSupport(): void {
  // 检查当前语言是否支持语音播报
  const currentLocale = getCookie('setting_lang') || 'en';
  const lang = localeToLangMap[currentLocale] || 'en-US';
  const voices = window.speechSynthesis.getVoices();
  const voicesArr = voices.filter(function (voice) {
    return voice.lang == lang;
  });
  if (voicesArr.length == 0) {
    errorReport(`没有可用的${lang}语音`);
    voiceConfigWarn(lang);
  }
}

interface VoiceConfig {
  text: string;
  lang: string;
  vol?: number;
  rate?: number;
}

const voiceConfigWarn = (lang: string): void => {
  const h = vue.$createElement;
  const langDesc = langToDescMap[lang] || lang;
  Vue.prototype.$message({
    message: h('div', undefined, [
      i18n(
        'We detected your computer do not support %s language. To ensure any system voice remindings of %s language can be played normally, please add the language in your computer setting - language module.',
        [langDesc, langDesc]
      ),
      h(
        's-button',
        {
          props: {
            type: 'text',
          },
          on: {
            click() {
              const a = document.createElement('a');
              a.href = docUrl;
              a.target = '_blank';
              a.click();
            },
          },
        },
        i18n('Guide')
      ),
    ]),
    type: 'warning',
  });
};

// 文字合成语音
export function textToVoice(config: VoiceConfig): void {
  window.speechSynthesis.cancel();
  const instance = new window.SpeechSynthesisUtterance();
  instance.text = config.text; // 文字内容
  instance.lang = config.lang || 'en-US'; // 语言
  const voices = window.speechSynthesis.getVoices();
  const voicesArr = voices.filter(function (voice) {
    return voice.lang == config.lang;
  });
  if (voicesArr.length == 0) {
    instance.lang = 'en-US';
    errorReport(`没有可用的${config.lang}语音!`);
  } else {
    instance.voice = voicesArr[0];
  }
  instance.volume = config.vol || 1; // 音量
  instance.rate = config.rate || 1; // 语速
  instance.pitch = 1; // 音高
  window.speechSynthesis.speak(instance); // 播放
}

export function textToVoiceWithJudge(config: VoiceConfig): boolean {
  window.speechSynthesis.cancel();
  const instance = new window.SpeechSynthesisUtterance();
  instance.text = config.text; // 文字内容
  instance.lang = config.lang || 'en-US'; // 语言
  const voices = window.speechSynthesis.getVoices();
  const voicesArr = voices.filter(function (voice) {
    return voice.lang == config.lang;
  });

  if (voicesArr.length == 0) {
    instance.lang = 'en-US';
    errorReport(`没有可用的${config.lang}语音!`);
    return false;
  } else {
    instance.voice = voicesArr[0];
  }
  instance.volume = config.vol || 1; // 音量
  instance.rate = config.rate || 1; // 语速
  instance.pitch = 1; // 音高
  window.speechSynthesis.speak(instance); // 播放
  return true;
}

export function textToVoiceWithi18n(text: string): void {
  // 默认使用当前设置的语言播报
  // 要求：(1)transify存在相应语言的翻译 (2)当前电脑已配置相应语言包
  // 如果不同时满足这两个条件，则使用英语播报
  const currentLocale = getCookie('setting_lang') || 'en';
  const translatedText = i18n(text);
  const lang = localeToLangMap[currentLocale] || 'en-US';
  const config = {
    text: translatedText,
    lang,
  };
  // 兜底1：如果没有对应的翻译，使用英语播报
  if (text === translatedText) {
    config.text = 'error';
    config.lang = 'en-US';
  }
  // 兜底2：如果没有可用的语音，使用英语播报
  if (!textToVoiceWithJudge(config)) {
    config.text = 'error';
    config.lang = 'en-US';
    textToVoice(config);
  }
}

export function errorNoticeVoice(): void {
  const text = 'play-error'; // play-error 是专门为错误语音播报准备的transify-key,默认已设置好
  textToVoiceWithi18n(text);
}
